import { sortedUnique } from './collections.js';
/** `source` on a particle a perk brought with it, so it is never mistaken for authored content. */
export const PERK_PARTICLE_SOURCE = 'perk';
/**
 * The perks named by `equipped`, in catalogue order.
 *
 * Throws when more are equipped than there are slots, or when an id is not in
 * the catalogue. Both are caller bugs rather than content problems — the career
 * screen enforces the slot count, and the save layer writes the ids — and
 * silently dropping one would make a run score differently from the run the
 * player thought they were playing.
 */
export function equippedPerks(catalogue, equipped) {
    const ids = sortedUnique(equipped);
    if (ids.length > catalogue.equipSlots) {
        throw new Error(`${String(ids.length)} perks equipped against ${String(catalogue.equipSlots)} slots: ${ids.join(', ')}`);
    }
    const unknown = ids.filter((id) => !catalogue.perks.some((perk) => perk.id === id));
    if (unknown.length > 0) {
        throw new Error(`Unknown perk id(s): ${unknown.join(', ')}`);
    }
    return catalogue.perks.filter((perk) => ids.includes(perk.id));
}
/**
 * Folds the equipped perks' effects into resolved settings.
 *
 * Deltas accumulate — two perks that each widen the strike budget widen it
 * twice — while overrides are last-wins in catalogue order, which is stable
 * because the catalogue is data and its order is authored.
 *
 * `phaseBudgetMultiplierDelta` is deliberately absent: the phase budget is
 * derived from the pack's declared budget at `createGame`, so a perk that
 * widens it has to be applied there, before the multiplication. `createGame`
 * reads the delta out of the effects directly.
 */
export function applyPerkEffects(settings, perks) {
    let next = settings;
    for (const perk of perks) {
        const effects = perk.effects;
        next = {
            ...next,
            tagChips: effects.tagChips ?? next.tagChips,
            blocksAttributionMutation: next.blocksAttributionMutation || effects.blocksAttributionMutation === true,
            overridesNodeTemperature: next.overridesNodeTemperature || effects.overridesNodeTemperature === true,
            strikeBudget: Math.max(1, next.strikeBudget + (effects.strikeBudgetDelta ?? 0)),
            suspicionMultiplier: Math.max(0, next.suspicionMultiplier + (effects.suspicionMultiplierDelta ?? 0)),
            // A hint threshold of `null` is the profile disabling the ladder outright.
            // A perk moves a threshold; it does not switch the ladder back on.
            hintThreshold: next.hintThreshold === null
                ? null
                : Math.max(0, next.hintThreshold + (effects.hintThresholdDelta ?? 0)),
            scoreMultiplier: Math.max(0, next.scoreMultiplier + (effects.scoreMultiplierDelta ?? 0)),
        };
    }
    return { ...next, equippedPerks: sortedUnique(perks.map((perk) => perk.id)) };
}
/** The combined `phaseBudgetMultiplierDelta` of the equipped perks. */
export function phaseBudgetDeltaOf(perks) {
    return perks.reduce((total, perk) => total + (perk.effects.phaseBudgetMultiplierDelta ?? 0), 0);
}
/** The particle a perk grants, rendered as a package particle. */
function particleOf(effects) {
    const granted = effects.grantsParticle;
    if (granted === undefined)
        return null;
    return {
        id: granted.id,
        slotRole: granted.slotRole,
        text: granted.text,
        tags: granted.tags,
        source: PERK_PARTICLE_SOURCE,
        // Never filtered out by a profile's decoy ceiling: the player paid for it.
        decoyTier: 0,
    };
}
/** The ids of every particle the equipped perks grant, sorted. */
export function perkParticleIds(perks) {
    return sortedUnique(perks
        .map((perk) => perk.effects.grantsParticle?.id)
        .filter((id) => id !== undefined));
}
/**
 * A copy of `pack` with the equipped perks' particles spliced in.
 *
 * A perk particle that collides with an authored id is dropped in favour of the
 * package's own: a mission that has already written a particle under that id
 * has rules pointing at it, and replacing it would change what the mission
 * means. Returns the package unchanged when no perk grants anything, so the
 * common path allocates nothing.
 */
export function packWithPerks(pack, perks) {
    const additions = perks
        .map((perk) => particleOf(perk.effects))
        .filter((particle) => particle !== null)
        .filter((particle) => !pack.particles.some((existing) => existing.id === particle.id));
    if (additions.length === 0)
        return pack;
    return { ...pack, particles: [...pack.particles, ...additions] };
}
//# sourceMappingURL=perks.js.map